import { useCallback, useMemo, useState } from "react";
import apiClient from "../../../../../../application/apis/api-client";
import { toast } from "react-toastify";
import { reportUnexpectedError } from "@graffer-inc/smart-apply";
import { ProcedureImportStatusWithDestManageGroupIdType } from "../../../../../../application/domain/procedures/procedure-import-status";

type props = {
  tenantId: string;
};

type jsonFileWithCheckResultType = {
  file: File;
  destManageGroupId: ProcedureImportStatusWithDestManageGroupIdType["destManageGroupId"];
  checkResult: Omit<
    ProcedureImportStatusWithDestManageGroupIdType,
    "hasInvalidZip" | "hasInvalidFileVersion"
  >;
};

type filename = string;

type checkResultType = {
  invalidJsons: filename[];
  invalidFormSchemas: filename[];
  noPermissionDestManageGroups: filename[];
  duplicatedCustomPaths: filename[];
};

type bulkImportResultType = {
  successes: {
    procedureId: string;
    procedureName: string;
  }[];
  failures: filename[];
};

export const useServiceProviderSmartApplyProceduresBulkImportByCSVHooks = ({
  tenantId,
}: props) => {
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [isCheckingImportJsonFiles, setIsCheckingImportJsonFiles] =
    useState(false);

  const [showResultMode, setShowResultMode] = useState<
    "showCheckResult" | "showBulkImportResult"
  >("showCheckResult");

  const importableLimit = 50;
  const allowFileType = "application/json";

  // import事前確認結果に関連するstate
  //
  // 個々のエラーstateを管理すると煩雑になるので、ファイルとチェック結果をセットで管理する
  // このstateをメモ化したものを表示側に返す
  const [jsonFilesWithCheckResult, setJsonFilesWithCheckResult] = useState<
    jsonFileWithCheckResultType[]
  >([]);

  const jsonFiles = useMemo(
    () => jsonFilesWithCheckResult.map((f) => f.file),
    [jsonFilesWithCheckResult],
  );

  const checkResult: checkResultType = useMemo(() => {
    const invalidJsons = jsonFilesWithCheckResult
      .filter((r) => r.checkResult.hasInvalidJson)
      .map((r) => r.file.name);
    const invalidFormSchemas = jsonFilesWithCheckResult
      .filter((r) => r.checkResult.hasInvalidFormSchema)
      .map((r) => r.file.name);
    const noPermissionDestManageGroups = jsonFilesWithCheckResult
      .filter((r) => r.checkResult.noPermissionDestManageGroup)
      .map((r) => r.file.name);
    const duplicatedCustomPaths = jsonFilesWithCheckResult
      .filter((r) => r.checkResult.hasDuplicateCustomPath)
      .map((r) => r.file.name);

    return {
      invalidJsons,
      invalidFormSchemas,
      noPermissionDestManageGroups,
      duplicatedCustomPaths,
    };
  }, [jsonFilesWithCheckResult]);

  const displayCheckResult =
    checkResult.invalidJsons.length > 0 ||
    checkResult.invalidFormSchemas.length > 0 ||
    checkResult.noPermissionDestManageGroups.length > 0 ||
    checkResult.duplicatedCustomPaths.length > 0;

  const hasCheckErr =
    checkResult.invalidJsons.length > 0 ||
    checkResult.invalidFormSchemas.length > 0 ||
    checkResult.noPermissionDestManageGroups.length > 0;

  const importable =
    !isBulkImporting &&
    !isCheckingImportJsonFiles &&
    jsonFiles.length > 0 &&
    !hasCheckErr;

  // import結果に関連するstate
  const [bulkImportResult, setBulkImportResult] =
    useState<bulkImportResultType>({
      successes: [],
      failures: [],
    });

  const checkJsonFile = async (
    file: File,
  ): Promise<jsonFileWithCheckResultType> => {
    try {
      const { data } =
        await apiClient.checkProcedureImportFileWithDestManageGroupId(
          file,
          tenantId,
        );
      return {
        file: file,
        destManageGroupId: data.destManageGroupId,
        checkResult: data,
      };
    } catch (e) {
      reportUnexpectedError(e);

      // jsonファイルを読み込みbackendで設定内容を判定して200を返すため、基本的には想定していないエラーとなる
      // 大域を抜ける(handleSetJsonFilesにcatchさせる)ためにrethrowする
      throw e;
    }
  };

  const readJsonFile = (file: File): Promise<jsonFileWithCheckResultType> => {
    return new Promise<jsonFileWithCheckResultType>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () =>
        new Promise(() => {
          try {
            const result = checkJsonFile(file);
            resolve(result);
          } catch (e) {
            reject(e);
          }
        });
      reader.readAsText(file);
    });
  };

  const handleSetJsonFiles = useCallback(
    async (files: File[]) => {
      setIsCheckingImportJsonFiles(true);

      if (files.length === 0) return;
      if (files.length > importableLimit) {
        toast.error(`一度に登録可能な件数は最大${importableLimit}件です。`);
        setIsCheckingImportJsonFiles(false);
        return;
      }
      if (files.some((f) => f.type !== allowFileType)) {
        toast.error("json形式のファイルをアップロードしてください。");
        setIsCheckingImportJsonFiles(false);
        return;
      }

      try {
        const promises = files.map(async (f) => {
          const result = await readJsonFile(f);
          return result;
        });

        const results = await Promise.all(promises);
        setJsonFilesWithCheckResult(results);
      } catch (e) {
        toast.error("ファイル読み込み時にエラーが発生しました。");
      } finally {
        setIsCheckingImportJsonFiles(false);
      }
    },
    [readJsonFile],
  );

  const importJsonFile = async (
    file: File,
    tenantId: string,
    destManageGroupId: string | null,
  ): Promise<
    | {
        id: string;
        name: string;
      }
    | {
        filename: string;
      }
  > => {
    try {
      // 移行先管理グループ設定に不備がある場合は一括インポートボタンをdisableにしているので、この分岐には入らない想定
      // 念の為エラーとしてハンドリングを入れておく
      if (!destManageGroupId) {
        throw new Error("destManageGroupId is required.");
      }

      const { data } = await apiClient.importProcedureFromFile(
        file,
        tenantId,
        destManageGroupId,
        checkResult.duplicatedCustomPaths.includes(file.name),
      );
      return { id: data.id, name: data.name };
    } catch (e) {
      reportUnexpectedError(e);
      return { filename: file.name };
    }
  };

  const handleBulkImport = async () => {
    if (!importable) return;
    if (!window.confirm("一括インポートを実行します。よろしいですか？")) return;

    setIsBulkImporting(true);
    setJsonFilesWithCheckResult([]);

    try {
      const promises = jsonFilesWithCheckResult.map(async (r) => {
        const result = await importJsonFile(
          r.file,
          tenantId,
          r.destManageGroupId,
        );
        return result;
      });

      const bulkImportResults = await Promise.all(promises);

      const successes = [] as bulkImportResultType["successes"];
      const failures = [] as bulkImportResultType["failures"];

      bulkImportResults.forEach((r) => {
        if ("filename" in r) {
          failures.push(r.filename);
        } else {
          successes.push({ procedureId: r.id, procedureName: r.name });
        }
      });

      setShowResultMode("showBulkImportResult");
      setBulkImportResult({
        successes,
        failures,
      });

      toast.success(`${successes.length}件の手続きをインポートしました。`);
    } catch (e) {
      toast.error("一括インポート時にエラーが発生しました。");
    } finally {
      setIsBulkImporting(false);
    }
  };

  const handleDelete = () => {
    setJsonFilesWithCheckResult([]);
  };

  return {
    isBulkImporting,
    isCheckingImportJsonFiles,
    showResultMode,
    importableLimit,
    jsonFiles,
    checkResult,
    displayCheckResult,
    hasCheckErr,
    importable,
    bulkImportResult,
    handleSetJsonFiles,
    handleBulkImport,
    handleDelete,
  };
};
